import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { adminApi, errorMessage } from "../../lib/api";
import { inr } from "../landing/usePricing";
import { DocPreview } from "./DocPreview";
import { ServiceRates } from "./ServiceRates";
import { HkEmployees } from "./HkEmployees";
import { HkPayouts } from "./HkPayouts";
import { AssignPicker } from "./AssignPicker";
import { useAuth } from "../../context/AuthContext";

const SLOTS = ["08:00-12:00", "12:00-16:00", "16:00-20:00"];
const OPEN = ["pending_payment", "confirmed", "assigned"];

const Actions = ({ b, act, employees }) => {
  const [quote, setQuote] = useState("");
  const [alt, setAlt] = useState([{ date: "", slot: SLOTS[0] }, { date: "", slot: SLOTS[1] }]);
  const setAltAt = (i, k, v) => setAlt((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const inp = "h-8 border border-white/15 bg-[#0B1120] px-2 text-sm";
  return (
    <div className="space-y-2 text-sm">
      {b.status === "pending_payment" && (
        <div className="flex gap-2"><input value={quote} onChange={(e) => setQuote(e.target.value.replace(/\D/g, ""))} placeholder="Custom total ₹" data-testid={`hk-admin-quote-input-${b.id}`} className={inp} />
          <button disabled={!quote} onClick={() => act(`/admin/hk/bookings/${b.id}/quote`, "Custom quote sent", { total: Number(quote) })} data-testid={`hk-admin-quote-${b.id}`} className="border border-white/15 px-3">Set quote</button></div>
      )}
      {["confirmed", "assigned"].includes(b.status) && (
        <AssignPicker key={employees.length} b={b} employees={employees} onAssign={(refs) => act(`/admin/hk/bookings/${b.id}/assign`, "Employees assigned", { worker_references: refs })} />
      )}
      {OPEN.includes(b.status) && (
        <div className="flex flex-wrap items-center gap-2">
          {alt.map((a, i) => (
            <span key={i} className="flex gap-1">
              <input type="date" value={a.date} onChange={(e) => setAltAt(i, "date", e.target.value)} data-testid={`hk-admin-alt-date-${b.id}-${i}`} className={`${inp} [color-scheme:dark]`} />
              <select value={a.slot} onChange={(e) => setAltAt(i, "slot", e.target.value)} className={inp}>{SLOTS.map((s) => <option key={s}>{s}</option>)}</select>
            </span>
          ))}
          <button onClick={() => act(`/admin/hk/bookings/${b.id}/reschedule-request`, "Reschedule request sent", { slots: alt.filter((a) => a.date) })} data-testid={`hk-admin-resched-${b.id}`} className="border border-sky-400/60 px-3 py-1 text-sky-300">Send reschedule request</button>
          <button onClick={() => window.confirm(`Cancel ${b.reference} and refund 100% advance?`) && act(`/admin/hk/bookings/${b.id}/cancel-refund`, "Cancelled & refunded")} data-testid={`hk-admin-cancel-${b.id}`} className="bg-red-600 px-3 py-1 font-semibold text-white">Cancel & Refund</button>
        </div>
      )}
    </div>
  );
};

const Booking = ({ b, act, employees }) => (
  <article data-testid={`hk-admin-booking-${b.id}`} className="space-y-2 border border-white/10 p-4">
    <div className="flex flex-wrap justify-between gap-2">
      <p className="font-semibold text-white">{b.reference} · {b.service_label}{b.qty > 1 ? ` × ${b.qty}` : ""}{b.days > 1 ? ` × ${b.days} days` : ""}</p>
      <span data-testid={`hk-admin-status-${b.id}`} className="bg-white/5 px-2 text-xs">{b.status}</span>
    </div>
    <p className="text-sm text-slate-400">{b.customer_name} · {b.customer_phone} · {b.date} {b.slot} · {b.address}</p>
    <p className="text-sm">{b.pricing.price_on_request ? <span className="text-amber-300">Price on request: set a custom quote</span> : <>Total {inr(b.pricing.total)} · advance {inr(b.pricing.advance)}</>} · paid {inr(b.payments.reduce((s, p) => s + p.amount, 0))}{b.rating ? ` · ${b.rating}★` : ""}{b.worker_names ? ` · team: ${b.worker_names.join(", ")}` : ""}</p>
    {b.shifts.map((s) => (
      <div key={s.worker_reference} className="space-y-2 bg-black/20 p-2 text-xs">
        <p>{s.worker_name}: in {new Date(s.check_in).toLocaleTimeString("en-IN")}{s.check_out ? ` · out ${new Date(s.check_out).toLocaleTimeString("en-IN")} · ${s.hours} h · payout ${inr(s.total_payout)} (wage ${s.wage} + OT ${s.overtime_pay} + travel ${s.travel_allowance} + bonus ${s.bonus})` : " · working"}</p>
        {s.check_out && <div className="grid grid-cols-2 gap-2"><DocPreview fileId={s.before_photo_id} label="Before" testId={`hk-before-photo-${b.id}`} /><DocPreview fileId={s.after_photo_id} label="After" testId={`hk-after-photo-${b.id}`} /></div>}
      </div>
    ))}
    {b.messages?.length > 0 && <p className="text-xs text-slate-500">Last message: {b.messages[b.messages.length - 1].body} (SMS {b.messages[b.messages.length - 1].sms})</p>}
    <Actions b={b} act={act} employees={employees} />
  </article>
);

export const HousekeepingDialog = ({ open, onClose }) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [tab, setTab] = useState("bookings");
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const load = useCallback(() => adminApi.get("/admin/hk/bookings", { params: status ? { status } : {} }).then((r) => setItems(r.data)).catch(() => {}), [status]);
  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => {
    if (open) adminApi.get("/admin/hk/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, [open]);
  const act = (path, msg, body) => adminApi.post(path, body).then(() => { toast.success(msg); load(); }).catch((e) => toast.error(errorMessage(e)));
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="hk-admin-dialog" className="max-h-[90vh] max-w-5xl overflow-y-auto rounded-none border-white/10 bg-[#0F172A] text-slate-100">
        <DialogHeader><DialogTitle className="font-heading text-xl text-white">Services & bookings <span className="text-sm font-normal text-emerald-300">(UPI payments)</span></DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2 border-b border-white/10 pb-3">
          {[["bookings", "Bookings"], ["employees", "Employees"], ...(user?.is_owner ? [["payouts", "Payouts"], ["rates", "Service rates"]] : [])].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`hk-tab-${k}`} className={`px-4 py-1.5 text-sm ${tab === k ? "bg-orange-500 text-white" : "border border-white/10 text-slate-300"}`}>{l}</button>
          ))}
        </div>
        {tab === "rates" ? <ServiceRates /> : tab === "employees" ? <HkEmployees /> : tab === "payouts" ? <HkPayouts /> : <>
        <div className="flex flex-wrap gap-2">
          {["", "pending_payment", "confirmed", "assigned", "in_progress", "completed", "cancelled"].map((s) => (
            <button key={s || "all"} onClick={() => setStatus(s)} data-testid={`hk-admin-filter-${s || "all"}`} className={`border px-3 py-1 text-xs ${status === s ? "border-orange-500 text-orange-300" : "border-white/10 text-slate-400"}`}>{s || "all"}</button>
          ))}
        </div>
        {items.length === 0 && <p className="py-6 text-center text-slate-500">No bookings.</p>}
        <div className="space-y-3">{items.map((b) => <Booking key={b.id} b={b} act={act} employees={employees} />)}</div>
        </>}
      </DialogContent>
    </Dialog>
  );
};
